// Copyright (c) Docugami, Inc. All rights reserved.

import { IListProps } from "@fluentui/react"
import { scrollIntoViewIfNeeded } from "components/doc_hosting/taskpane/TaskpaneCardHelpers"
import { useReportDocumentContext } from "components/reports/ReportDocumentContext"
import { addEntryThunk } from "components/reports/reportsActions"
import { getChunkLabel } from "hooks/LabelChunk"
import { useModalSelectionProvider } from "hooks/ModalSelection"
import { useChunkSelection } from "hooks/chunkSelection"
import { usePersistScrollPosition } from "hooks/persistScrollPosition"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useReportResizeHandler } from "hooks/reportChunkResizeHandler"
import { useReportEntryActions } from "hooks/reportEntryActions"
import { useReportEntryIdForChunk } from "hooks/reportEntryIdForChunk"
import { ReportEntry, ReportEntryGroup } from "networking/reportsApi"
import { Ref, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { AppDispatch, AppState } from "store"
import { ModalSelectionItemType } from "store/documents/types"
import { createOperationId, reportsApiSlice, selectPatchedReportSchemaState, setSelectedLabel } from "store/reports/reportsSlice"
import { selectIsReportPublished, selectReportEntryBindingsForCurrentDocument, selectReportId, selectReportSchema, selectSelectedLabel } from "store/reports/selectors"
import { selectSelectedChunkInstance } from "store/sdvSelectors"
import styled from "styled-components"
import { DndList } from "./DndList"
import { DndItem, DndType, DropTypes, isDndChunk, isDndReportItem, isDndReportItemGroup } from "./DndTypes"
import { ISlotItem, isReportChunkSlot } from "./ItemSlot"
import { ReportEntryItemCardlet } from "./ReportEntryItemCardlet"
import { ReportEntryItemGroup } from "./ReportEntryItemGroup"
import { IReportEntryItem, IReportEntryItemGroup, IReportItem, ReportItemType, isReportEntryGroupItem, isReportEntryItem } from "./ReportItemCardlet"

const ItemRoot = styled.div`
    margin-top: 5px;
`

interface SelectedEntry {
    entryId: string;
    instanceId?: string;
}

interface ScrollState {
    scrollPosition?: number;
    listHeight?: number;
    isInitialized?: boolean;
}

export interface ReportItemsListProps {
    clusterId: string;
    documentId: string;
    reportId: string;
    isDisabled?: boolean;
    reportItems: ReportEntry[];
    canDrop?: boolean;
}

export interface ReportEntryProviderItem {
    entry: ReportEntry;
    instanceId?: string;
}

export const ReportItemsList = (props: ReportItemsListProps): JSX.Element => {
    const reportId = useAppSelector(selectReportId);
    const schema = useAppSelector(selectReportSchema);
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);
    const isPublished = useAppSelector(selectIsReportPublished);

    const [scrollState, setScrollState] = useState<ScrollState>({});
    const [selectedEntry, setSelectedEntry] = useState<SelectedEntry | null>(null);

    useReportResizeHandler();
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const { clearSelection, selectChunk } = useChunkSelection();
    const entryIdForSelectedChunk = useReportEntryIdForChunk(selectedChunkInstance);
    const selectedLabel = useAppSelector(selectSelectedLabel);

    const { canAddEntry } = useReportEntryActions();

    const dispatch = useAppDispatch();

    const activeAddEntryGroupRequest = useAppSelector(state => state.reportOperations.requests.activeAddEntryGroupRequest);

    const chunkLinkingProps = useReportDocumentContext().chunkLinkingState;
    const { isLinkingChunk, startLinkingEntry, canLinkEntry } = chunkLinkingProps;
    const { isActive: isEntryProviderActive, setItemReady: setEntryProviderItemReady } = useModalSelectionProvider<ReportEntryProviderItem>(
        [ModalSelectionItemType.Entry],
        async () => {
            const entry = schema?.entries.entities[selectedEntry?.entryId ?? ''];
            return !!entry ? { entry: entry, instanceId: selectedChunkInstance?.spanId } : undefined
        });

    const listRootRef = useRef<HTMLDivElement>(null);

    function canDrop(item: DndItem) {
        if (!props.canDrop) {
            return false;
        }

        if (isPublished) {
            return false;
        }

        if (!canAddEntry) {
            return false;
        }

        if (isDndChunk(item)) {
            return !!dispatch(getChunkLabel(item.instance));
        }

        return true;
    }

    const items = useMemo((): IReportItem[] => {
        function getReportItemForEntry(entry: ReportEntry): IReportEntryItem {
            const isSelected = selectedEntry?.entryId === entry.id;
            const disabled = props.isDisabled ||
                (isLinkingChunk && !isSelected) ||
                (isEntryProviderActive && !canLinkEntry(entry)) ||
                !!selectedLabel;
            const isLinkTarget = isEntryProviderActive && canLinkEntry(entry);

            const item: IReportEntryItem = {
                id: entry.id,
                reportId: props.reportId,
                type: ReportItemType.ReportEntry,
                isSelected: isSelected,
                isDisabled: disabled,
                isLinkable: isLinkTarget,
                entry: entry,
                isEvaluating: false
            };

            return item;
        }

        function getReportItemForGroup(group: ReportEntryGroup): IReportEntryItemGroup {
            const item: IReportEntryItemGroup = {
                id: group.id,
                name: group.label,
                reportId: props.reportId,
                type: ReportItemType.ReportEntryGroup,
                groupItems: []
            };

            return item;
        }

        function tryGetGroup(groupId?: string): ReportEntryGroup | undefined {
            if (!groupId) {
                return;
            }

            return schema?.groups.entities[groupId];
        }

        const items: IReportItem[] = [];
        for (const entry of props.reportItems) {
            const group = tryGetGroup(entry.groupId);
            if (!!group) {
                // Special handling for the active add group request
                if (group.id === activeAddEntryGroupRequest?.id) {
                    if (activeAddEntryGroupRequest.entries[0] === entry.id) {
                        const groupItem = getReportItemForGroup(group);
                        for (const entryId of group.entries) {
                            const entry = schema?.entries.entities[entryId];
                            if (!!entry) {
                                groupItem.groupItems.push(getReportItemForEntry(entry));
                            }
                        }
                        items.push(groupItem);
                    }

                    continue;
                }

                let insertedGroupItem: IReportEntryItemGroup | undefined = items.find(x => isReportEntryGroupItem(x) && x.id === group.id) as IReportEntryItemGroup;
                if (!insertedGroupItem) {
                    // Group has not yet been added to items list
                    insertedGroupItem = getReportItemForGroup(group);
                    items.push(insertedGroupItem);
                }

                insertedGroupItem.groupItems.push(getReportItemForEntry(entry));
            } else {
                items.push(getReportItemForEntry(entry))
            }
        }

        return items;
    }, [selectedEntry?.entryId, isLinkingChunk, isEntryProviderActive, canLinkEntry, props.isDisabled, props.reportId, props.reportItems, schema?.groups.entities, schema?.entries.entities, activeAddEntryGroupRequest?.id, activeAddEntryGroupRequest?.entries, selectedLabel]);

    const onSelectReportItem = useCallback((item: IReportItem, slot?: ISlotItem) => {
        if (!isReportEntryItem(item)) {
            return;
        }

        if (!slot) {
            dispatch(setSelectedLabel(item.entry.title));
            return;
        }

        if (!isReportChunkSlot(slot)) {
            return;
        }

        if (!!slot.binding?.spanId) {
            setSelectedEntry({
                entryId: item.entry.id,
                instanceId: slot.binding?.spanId
            });
            selectChunk(slot.binding?.spanId);
        }
        else {
            clearSelection();
            setSelectedEntry({
                entryId: item.entry.id
            });

            if (!isEntryProviderActive) {
                startLinkingEntry(item.entry, false);
            }
        }
    }, [selectChunk, startLinkingEntry, clearSelection, isEntryProviderActive, dispatch]);

    useEffect(() => {
        // Selected entry is removed
        if (!!selectedEntry) {
            if (!schema?.entries.entities[selectedEntry.entryId]) {
                setSelectedEntry(null);
            }
        }
        // eslint-disable-next-line
    }, [schema?.entries]);

    useEffect(() => {
        // If providing an entry then set the ready state based on whether an entry is currently selected
        if (isEntryProviderActive) {
            setEntryProviderItemReady(!!selectedEntry?.entryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntry]);

    useEffect(() => {
        // Update selected report item based on currently selected chunk.
        // Handle the case that an entry with no bound chunk is selected
        setSelectedEntry(x => {
            if ((!selectedChunkInstance && !x?.instanceId) || (isLinkingChunk && !!x?.entryId)) {
                return x;
            }

            if (!!selectedChunkInstance) {
                // Chunks can be bound to multiple entries, and the entryIdForSelectedChunk selector always chooses the first bound entry.
                // If there is a previously selected entry id, and the selected chunk is bound to it, that means the user has specifically
                // selected that entry for the selected chunk, so we should respect the entry selection.
                if (!!x?.entryId && documentBindings?.entities[x.entryId]?.bindings.find(b => b.spanId === selectedChunkInstance.spanId)) {
                    return {
                        entryId: x.entryId,
                        instanceId: selectedChunkInstance.spanId
                    };
                }

                if (!!entryIdForSelectedChunk) {
                    return {
                        entryId: entryIdForSelectedChunk,
                        instanceId: selectedChunkInstance.spanId
                    }
                }
            }

            return null;
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChunkInstance, entryIdForSelectedChunk, isLinkingChunk]);

    useEffect(() => {
        if (!listRootRef.current || !!selectedLabel) {
            return;
        }

        if (!!selectedEntry?.entryId) {
            const child = listRootRef.current.querySelector(`#id_${selectedEntry.entryId}`);
            if (!!child) {
                scrollIntoViewIfNeeded(child, listRootRef.current)
            }

            setScrollState(x => !!x.isInitialized ? x : ({ ...x, isInitialized: true }));
        }
    }, [selectedEntry, selectedLabel])

    useLayoutEffect(() => {
        listRootRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }, [props.documentId])

    useLayoutEffect(() => {
        if (!listRootRef.current || !!selectedLabel) {
            return;
        }

        if (!selectedEntry?.entryId && !scrollState.isInitialized && scrollState.scrollPosition !== undefined && scrollState.listHeight !== undefined && scrollState.listHeight >= scrollState.scrollPosition) {
            listRootRef.current.scrollTo({ top: scrollState.scrollPosition, behavior: 'auto' });
            setScrollState(x => ({ ...x, isInitialized: true }));
        }
    }, [scrollState, selectedEntry, selectedLabel]);

    usePersistScrollPosition("reportItemsList",
        () => listRootRef.current?.scrollTop,
        value => setScrollState({ scrollPosition: value }));

    const dropTypes = useMemo(() => isPublished ? [] : [DndType.Chunk, DndType.ReportItem, DndType.ReportItemGroup], [isPublished]);

    return (
        <ReportItemsListVisual
            {...props}
            rootRef={listRootRef}
            items={items}
            onSelectReportItem={onSelectReportItem}
            onDndDrop={(dropItem, index, item, groupId) => dispatch(onDropReportItem(reportId, dropItem, index, groupId))}
            canDndDrop={canDrop}
            dropTypes={dropTypes}
            readonly={isPublished}
            onRenderPage={(props, defaultRender) => {
                if (!scrollState.isInitialized && !!listRootRef.current) {
                    setScrollState(x => ({ ...x, listHeight: listRootRef.current!.scrollHeight }));
                }

                return defaultRender?.(props) ?? null;
            }} />
    )
}

interface ReportItemsListVisualProps extends IListProps<IReportItem> {
    items: IReportItem[];
    clusterId: string,
    reportId: string,
    documentId: string,
    dropTypes: DropTypes;
    rootRef?: Ref<HTMLDivElement>;
    onDndDrop?: (dropItem: DndItem, index: number, item?: IReportItem, groupId?: string) => void;
    canDndDrop?: (item: DndItem) => boolean;
    onSelectReportItem: (item: IReportItem, slot?: ISlotItem) => void;
    readonly?: boolean;
}

export const ReportItemsListVisual = (props: ReportItemsListVisualProps): JSX.Element => {
    function onRenderItem(item?: IReportItem, index?: number, isScrolling?: boolean): JSX.Element | null {
        if (!!item) {
            if (isReportEntryGroupItem(item)) {
                const group = item;

                // Cannot add a group to a group, leave groups out of allowed dropTypes
                const subListProps = {
                    ...props,
                    rootRef: undefined,
                    dropTypes: [DndType.Chunk, DndType.ReportItem],
                    onDndDrop: (dropItem: DndItem, index: number, item?: IReportItem) => props.onDndDrop?.(dropItem, index, item, group.id)
                }

                const getDisabledDropIndices = (dropItem: DndItem): number[] => {
                    let disabledIndices: number[] = [];

                    if (isDndReportItem(dropItem)) {
                        const dropItemIdx = group.groupItems.findIndex(item => item.id === dropItem.entryId);
                        if (dropItemIdx !== -1) {
                            // The entry is within the same group. We can only drop to reorder,
                            // so disable surrounding indices
                            disabledIndices = [dropItemIdx, dropItemIdx + 1];
                        }
                    }

                    return disabledIndices;
                }

                const dragDisabled = !props.dropTypes.includes(DndType.ReportItemGroup) || !!props.readonly;

                return (
                    <ReportEntryItemGroup group={group} dragDisabled={dragDisabled} readonly={props.readonly}>
                        <DndList
                            {...subListProps}
                            items={group.groupItems}
                            onRenderCell={onRenderReportItem}
                            disableDropIndices={getDisabledDropIndices}
                            enableEndListDropZone={false} />
                    </ReportEntryItemGroup>
                )
            } else if (isReportEntryItem(item)) {
                return onRenderReportItem(item, index, isScrolling);
            }
        }

        return null;
    }

    const { onSelectReportItem } = props;
    const onRenderReportItem = useCallback((item?: IReportItem, index?: number, isScrolling?: boolean): JSX.Element | null => {
        if (!!item) {
            const dragDisabled = !props.dropTypes.includes(DndType.ReportItem) || !!props.readonly;

            if (isReportEntryItem(item)) {
                return (
                    <ItemRoot id={`id_${item.entry.id}`}>
                        <ReportEntryItemCardlet
                            item={item}
                            onClick={(slot) => onSelectReportItem(item, slot)}
                            dragDisabled={dragDisabled} />
                    </ItemRoot>
                )
            }
        }

        return null;
    }, [onSelectReportItem, props.readonly, props.dropTypes]);

    function getDisabledDropIndices(dropItem: DndItem): number[] {
        let disabledIndices: number[] = [];

        const dropItemId = isDndReportItem(dropItem) ? dropItem.entryId : isDndReportItemGroup(dropItem) ? dropItem.groupId : '';
        const dropItemIdx = props.items.findIndex(item => item.id === dropItemId);

        if (dropItemIdx !== -1) {
            disabledIndices = [dropItemIdx, dropItemIdx + 1];
        }

        return disabledIndices;
    }

    return (
        <DndList
            {...props}
            onRenderCell={onRenderItem}
            rootRef={props.rootRef}
            disableDropIndices={getDisabledDropIndices}
            onShouldVirtualize={() => props.items.length > 15}
            enableEndListDropZone={true} />
    )
}

const onDropReportItem = (reportId: string, dropItem: DndItem, index: number, groupId?: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const schemaState = selectPatchedReportSchemaState(getState(), reportId);

    if (!schemaState) {
        return;
    }

    if (schemaState.report.isPublished) {
        return;
    }

    // Construct a list of visible items in report order
    const reportEntries = [...schemaState.report.entries];
    const normalizedItems: string[] = [];
    // also track the order of group entries
    const groupEntryOrders: { [id: string]: string[] } = {};
    for (const entryId of schemaState.report.entries) {
        const entry = schemaState.entries.entities[entryId]!;
        if (!!entry.groupId) {
            if (groupEntryOrders[entry.groupId] === undefined) {
                normalizedItems.push(entry.groupId);
                groupEntryOrders[entry.groupId] = [];
            }
            groupEntryOrders[entry.groupId].push(entry.id);
        } else {
            normalizedItems.push(entry.id);
        }
    }

    let targetIndex = -1;
    if (!!groupId) {
        // we are within a group, get the adjusted index from the group's start position
        const targetEntry = groupEntryOrders[groupId]?.[index];
        targetIndex = reportEntries.indexOf(targetEntry);
    } else {
        const targetId = normalizedItems[index];
        targetIndex = reportEntries.indexOf(targetId);
        if (!!targetId && targetIndex === -1) {
            // the target item id is a group, not an entry
            const startEntry = groupEntryOrders[targetId][0];
            targetIndex = reportEntries.indexOf(startEntry);
        }
    }

    if (targetIndex === -1) {
        // If there is no target index, fallback to end of list (or end of group if we are dropping within a group)
        targetIndex = !!groupId ? (groupEntryOrders[groupId]?.length ?? -1) : reportEntries.length;
    }

    if (isDndChunk(dropItem)) {
        dispatch(addEntryThunk(reportId, {
            folderId: dropItem.instance.folderId,
            instanceId: dropItem.instance.spanId!,
            label: dropItem.label,
            index: targetIndex,
            groupId: groupId
        }));
    }
    else if (isDndReportItem(dropItem)) {
        const originalIndex = reportEntries.indexOf(dropItem.entryId);
        let orderedEntries: string[] | undefined = undefined;
        if (originalIndex !== targetIndex) {
            // position has changed, create new ordering
            orderedEntries = [...reportEntries];
            const target = orderedEntries[targetIndex];
            const removed = orderedEntries.splice(originalIndex, 1);
            const indexToInsert = !!target ? orderedEntries.indexOf(target) : targetIndex;
            orderedEntries.splice(indexToInsert, 0, ...removed);
        }

        // do group mutations
        const entry = schemaState.entries.entities[dropItem.entryId]!;
        if (entry.groupId !== groupId) {
            if (!!groupId) {
                // add entry to group
                return dispatch(reportsApiSlice.endpoints.addEntriesToGroup.initiate({
                    reportId: reportId,
                    groupId: groupId,
                    entries: [entry.id],
                    orderedEntries: orderedEntries,
                    operationId: createOperationId()
                }));
            } else if (!!entry.groupId) {
                // remove entry from group
                return dispatch(reportsApiSlice.endpoints.removeEntriesFromGroup.initiate({
                    reportId: reportId,
                    groupId: entry.groupId,
                    entries: [entry.id],
                    orderedEntries: orderedEntries,
                    operationId: createOperationId()
                }));
            }
        }

        if (!!orderedEntries) {
            dispatch(reportsApiSlice.endpoints.setEntriesOrder.initiate({
                reportId: reportId,
                entryIds: orderedEntries,
                operationId: createOperationId()
            }));
        }
    }
    else if (isDndReportItemGroup(dropItem)) {
        if (!!groupId) {
            // cannot drag a group into another group
            return;
        }

        const groupEntries = groupEntryOrders[dropItem.groupId];
        // save the target before mutation
        const target = reportEntries[targetIndex];
        // cut out the group from the ordered entries list
        const groupStartIdx = reportEntries.indexOf(groupEntries[0]);
        const removed = reportEntries.splice(groupStartIdx, groupEntries.length);
        // put the group back in at the target
        const indexToInsert = !!target ? reportEntries.indexOf(target) : targetIndex;
        reportEntries.splice(indexToInsert, 0, ...removed);

        dispatch(reportsApiSlice.endpoints.setEntriesOrder.initiate({
            reportId: reportId,
            entryIds: reportEntries,
            operationId: createOperationId()
        }));

        return;
    }
}
